import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSession } from "@hooks/useSession";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Stack,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import { ErrorOutline as ErrorOutlineIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

export default function PaymentError() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { session } = useSession();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!session) {
      navigate("/");
      return;
    }
  }, [session, navigate]);

  const handleTryAgain = () => {
    navigate("/subscription");
  };

  const handleReturnHome = () => {
    navigate("/");
  };

  const errorCode = searchParams.get("error_code") || "UNKNOWN";
  const errorMessage =
    searchParams.get("error_message") || t("paymentError.description");

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        py: 4,
        background: (theme) => ({
          backgroundImage:
            "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
          ...theme.applyStyles("dark", {
            backgroundImage:
              "radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
          }),
        }),
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.background.paper,
              0.9
            )} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.error.main, 0.1),
              color: "error.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 48 }} />
          </Box>

          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            fontWeight="bold"
            color="error"
          >
            {t("paymentError.title")}
          </Typography>

          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 400, mx: "auto" }}
          >
            {errorMessage}
          </Typography>

          <Stack spacing={2} sx={{ mb: 4 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.error.main, 0.05),
                border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
              }}
            >
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("paymentError.errorCode")}
              </Typography>
              <Typography variant="body1" color="error">
                {errorCode}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            sx={{ mt: 2 }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleTryAgain}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(
                  theme.palette.primary.main,
                  0.8
                )} 100%)`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.9
                  )} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
                  boxShadow: theme.shadows[8],
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              {t("paymentError.tryAgain")}
            </Button>

            <Button
              variant="outlined"
              size="large"
              onClick={handleReturnHome}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {t("paymentError.returnHome")}
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
